import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { openOAuthPopup } from '../utils/composioAuth';
import { useOrganization } from '../context/OrganizationContext';
import { toast } from 'sonner';
import {
  Check, CircleNotch, ArrowSquareOut, X,
  MagnifyingGlass, LinkBreak, Star, Key, CaretDown, CaretUp
} from '@phosphor-icons/react';

/* ═══════════════════════════════════════════════════════
   API KEY INLINE FORM
   ═══════════════════════════════════════════════════════ */
const ApiKeyForm = ({ app, onConnect, onClose, connecting }) => {
  const [apiKey, setApiKey] = useState('');
  return (
    <div className="p-4 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={app.logo} alt={app.name} className="w-6 h-6 rounded-lg" onError={e => { e.target.style.display = 'none'; }} />
          <span className="text-sm font-medium text-[#F0F4F8]">Connect {app.name}</span>
        </div>
        <button onClick={onClose} className="text-[#7D8896] hover:text-[#F0F4F8]"><X size={14} /></button>
      </div>
      <input
        type="password"
        value={apiKey}
        onChange={e => setApiKey(e.target.value)}
        placeholder={`Paste your ${app.name} API key`}
        className="w-full px-3 py-2 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
        data-testid="tools-api-key-input"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onConnect(app.slug, apiKey)}
          disabled={!apiKey.trim() || connecting}
          className="px-3 py-1.5 bg-[#00E5C0] text-[#12161C] rounded-lg text-xs font-bold hover:bg-[#00D4B0] disabled:opacity-40 flex items-center gap-1.5"
          data-testid="tools-api-key-submit"
        >
          {connecting ? <CircleNotch size={12} className="animate-spin" /> : <Key size={12} weight="bold" />}
          Connect
        </button>
        <button onClick={onClose} className="px-3 py-1.5 border border-[rgba(255,255,255,0.08)] text-[#B0B8C4] rounded-lg text-xs hover:bg-[rgba(255,255,255,0.04)]">Cancel</button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   APP ROW
   ═══════════════════════════════════════════════════════ */
const AppRow = ({ app, onConnect, onDisconnect, connecting }) => {
  const isConnecting = connecting === app.slug;
  return (
    <div
      data-testid={`tools-app-${app.slug}`}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
        app.connected
          ? 'bg-[rgba(0,229,192,0.03)] border-[rgba(0,229,192,0.1)]'
          : 'bg-[rgba(255,255,255,0.01)] border-[rgba(255,255,255,0.05)] hover:border-[rgba(255,255,255,0.1)]'
      }`}
    >
      <img
        src={app.logo} alt={app.name}
        className="w-7 h-7 rounded-lg object-contain shrink-0"
        onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(app.name)}&background=1C2230&color=E6EDF3&size=28&font-size=0.4`; }}
      />
      <span className="flex-1 text-xs font-medium text-[#F0F4F8] truncate">{app.name}</span>
      {app.auth_type === 'api_key' && !app.connected && (
        <span className="text-[9px] text-[#7D8896] bg-[rgba(255,255,255,0.04)] px-1.5 py-0.5 rounded shrink-0">API</span>
      )}
      {app.connected ? (
        <div className="flex items-center gap-1 shrink-0">
          <span className="flex items-center gap-1 px-2 py-0.5 bg-[rgba(0,229,192,0.08)] text-[#00E5C0] rounded text-[10px] font-medium border border-[rgba(0,229,192,0.15)]">
            <Check size={9} weight="bold" />Connected
          </span>
          <button onClick={() => onDisconnect(app.slug)} className="p-0.5 text-[#7D8896] hover:text-[#FF6B6B] rounded hover:bg-[rgba(255,107,107,0.08)] transition-all" title="Disconnect">
            <LinkBreak size={11} />
          </button>
        </div>
      ) : (
        <button
          onClick={() => onConnect(app)}
          disabled={isConnecting}
          className="px-2.5 py-1 bg-[#161B26] text-[#F0F4F8] rounded-lg text-[10px] font-medium border border-[rgba(255,255,255,0.1)] hover:bg-[#252D3D] hover:border-[rgba(255,255,255,0.2)] disabled:opacity-40 flex items-center gap-1 shrink-0"
        >
          {isConnecting ? <CircleNotch size={10} className="animate-spin" /> : <ArrowSquareOut size={10} />}
          Connect
        </button>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   CATEGORY GROUP
   ═══════════════════════════════════════════════════════ */
const CatGroup = ({ label, apps, onConnect, onDisconnect, connecting, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
  if (!apps.length) return null;
  const connectedCount = apps.filter(a => a.connected).length;

  return (
    <div className="mb-3">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-1 py-1.5 group">
        <div className="flex items-center gap-2">
          <h3 className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">{label}</h3>
          <span className="text-[9px] text-[#7D8896] bg-[rgba(255,255,255,0.04)] px-1 py-0.5 rounded">
            {connectedCount > 0 && <span className="text-[#00E5C0]">{connectedCount}/</span>}{apps.length}
          </span>
        </div>
        {open ? <CaretUp size={10} className="text-[#7D8896]" /> : <CaretDown size={10} className="text-[#7D8896]" />}
      </button>
      {open && <div className="space-y-1 mt-1">{apps.map(a => <AppRow key={a.slug} app={a} onConnect={onConnect} onDisconnect={onDisconnect} connecting={connecting} />)}</div>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   TOOLS MODAL
   ═══════════════════════════════════════════════════════ */
const ToolsModal = ({ isOpen, onClose }) => {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;

  const [allApps, setAllApps] = useState([]);
  const [popular, setPopular] = useState([]);
  const [categories, setCategories] = useState({});
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [totalConnected, setTotalConnected] = useState(0);
  const [apiKeyApp, setApiKeyApp] = useState(null);
  const [showConnectedOnly, setShowConnectedOnly] = useState(false);

  const fetchApps = useCallback(async () => {
    if (!orgId) return;
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (activeCategory) params.set('category', activeCategory);
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/integrations/apps?${params}`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        setAllApps(data.apps || []);
        setPopular(data.popular || []);
        setCategories(data.categories || {});
        setCategoryOrder(data.category_order || []);
        setTotalConnected(data.total_connected || 0);
      }
    } catch (err) {
      console.error('Error loading apps:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, search, activeCategory]);

  useEffect(() => { if (isOpen) { setLoading(true); fetchApps(); } }, [isOpen, fetchApps]);

  const handleConnect = async (app) => {
    if (app.auth_type === 'api_key' && !app.connected) { setApiKeyApp(app); return; }
    setConnecting(app.slug);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/integrations/connect/${app.slug}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ callback_url: `${window.location.origin}/oauth/callback?connected=${app.slug}` }) },
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        if (data.redirect_url) {
          toast.info('Opening authorization...');
          await openOAuthPopup(data.redirect_url);
          await new Promise(r => setTimeout(r, 2000));
          fetchApps();
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not connect');
      }
    } catch { toast.error('Something went wrong'); }
    finally { setConnecting(null); }
  };

  const handleApiKeyConnect = async (slug, apiKey) => {
    setConnecting(slug);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/integrations/connect/${slug}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ api_key: apiKey }) },
        orgId
      );
      if (res.ok) { toast.success(`${apiKeyApp?.name || slug} connected`); setApiKeyApp(null); fetchApps(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Connection failed'); }
    } catch { toast.error('Something went wrong'); }
    finally { setConnecting(null); }
  };

  const handleDisconnect = async (slug) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/integrations/disconnect/${slug}`, { method: 'POST' }, orgId);
      if (res.ok) { toast.success('Disconnected'); fetchApps(); }
    } catch { toast.error('Could not disconnect'); }
  };

  const grouped = useMemo(() => {
    const g = {};
    const nonPopular = search ? allApps : allApps.filter(a => !a.is_popular);
    nonPopular.forEach(app => { const cat = app.category || 'other'; if (!g[cat]) g[cat] = []; g[cat].push(app); });
    return g;
  }, [allApps, search]);

  const activeCategoryOptions = useMemo(() => {
    const counts = {};
    allApps.forEach(app => { const cat = app.category || 'other'; counts[cat] = (counts[cat] || 0) + 1; });
    return categoryOrder.filter(k => counts[k] > 0).map(k => ({ key: k, label: categories[k] || k, count: counts[k] }));
  }, [allApps, categoryOrder, categories]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center pt-[8vh] p-4" onClick={onClose} data-testid="tools-modal-overlay">
      <div
        className="bg-[#111827] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-lg shadow-2xl flex flex-col"
        style={{ maxHeight: '80vh' }}
        onClick={e => e.stopPropagation()}
        data-testid="tools-modal"
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-[rgba(255,255,255,0.08)] shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-bold text-[#F0F4F8]" data-testid="tools-modal-heading">Connected Tools</h2>
              <p className="text-[11px] text-[#7D8896] mt-0.5">Connect your apps to let workers take action</p>
            </div>
            <div className="flex items-center gap-2">
              {totalConnected > 0 && (
                <span className="flex items-center gap-1 px-2 py-1 bg-[rgba(0,229,192,0.08)] text-[#00E5C0] rounded-lg text-[10px] font-semibold border border-[rgba(0,229,192,0.15)]">
                  <Check size={10} weight="bold" />{totalConnected} connected
                </span>
              )}
              <button onClick={onClose} className="p-1.5 hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-colors text-[#7D8896] hover:text-[#F0F4F8]" data-testid="tools-modal-close">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-2">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8896]" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search apps..."
              className="w-full pl-8 pr-3 py-2 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
              data-testid="tools-modal-search"
            />
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => { setActiveCategory(''); setShowConnectedOnly(false); }}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all ${!activeCategory && !showConnectedOnly ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border-[rgba(0,229,192,0.3)]' : 'text-[#B0B8C4] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)]'}`}
            >All</button>
            {totalConnected > 0 && (
              <button
                onClick={() => { setShowConnectedOnly(!showConnectedOnly); setActiveCategory(''); }}
                data-testid="tools-filter-connected"
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all flex items-center gap-1 ${showConnectedOnly ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border-[rgba(0,229,192,0.3)]' : 'text-[#B0B8C4] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)]'}`}
              ><Check size={9} weight="bold" />Connected</button>
            )}
            {activeCategoryOptions.map(cat => (
              <button
                key={cat.key}
                onClick={() => { setActiveCategory(activeCategory === cat.key ? '' : cat.key); setShowConnectedOnly(false); }}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all ${activeCategory === cat.key ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border-[rgba(0,229,192,0.3)]' : 'text-[#B0B8C4] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)]'}`}
              >{cat.label}</button>
            ))}
          </div>
        </div>

        {/* Content — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 scrollbar-thin scrollbar-thumb-[rgba(255,255,255,0.06)] scrollbar-track-transparent">
          {/* API Key inline form */}
          {apiKeyApp && (
            <div className="mb-4">
              <ApiKeyForm app={apiKeyApp} onConnect={handleApiKeyConnect} onClose={() => setApiKeyApp(null)} connecting={!!connecting} />
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-12"><CircleNotch size={24} className="animate-spin text-[#00E5C0]" /></div>
          )}

          {/* Connected-only view */}
          {!loading && showConnectedOnly && (() => {
            const connectedApps = allApps.filter(a => a.connected);
            if (!connectedApps.length) return (
              <div className="text-center py-8"><p className="text-[#7D8896] text-xs">No apps connected yet</p></div>
            );
            return (
              <div className="space-y-1">
                {connectedApps.map(app => <AppRow key={app.slug} app={app} onConnect={handleConnect} onDisconnect={handleDisconnect} connecting={connecting} />)}
              </div>
            );
          })()}

          {/* Popular */}
          {!loading && !showConnectedOnly && !search && !activeCategory && popular.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 px-1 mb-2">
                <Star size={10} weight="fill" className="text-amber-400" />
                <h3 className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">Popular</h3>
              </div>
              <div className="space-y-1">{popular.map(app => <AppRow key={app.slug} app={app} onConnect={handleConnect} onDisconnect={handleDisconnect} connecting={connecting} />)}</div>
            </div>
          )}

          {/* Categories */}
          {!loading && !showConnectedOnly && categoryOrder.map(catKey => (
            <CatGroup
              key={catKey}
              label={categories[catKey] || catKey}
              apps={grouped[catKey] || []}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              connecting={connecting}
              defaultOpen={!!search || !!activeCategory}
            />
          ))}

          {/* Empty */}
          {!loading && !showConnectedOnly && allApps.length === 0 && search && (
            <div className="text-center py-8"><p className="text-[#7D8896] text-xs">No apps match "{search}"</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolsModal;
